import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Icon,
  ProgressBar,
  Tooltip
} from "@shopify/polaris";
import {
  CancelSmallMinor,
  ChevronDownMinor,
  ChevronUpMinor,
  CircleTickMinor
} from "@shopify/polaris-icons";
import "./SetupGuide.css";

/**
 * The dashboard's setup guide: steps grouped into "Get started" and "Optional", one open at a time with its
 * graphic. It collapses once every step is done; the chevron opens it again to review or un-tick steps.
 *
 * steps: [{ key, title, description, media, completed, optional, canToggle, actions: [{ content, url, external, onAction }], notice }]
 * A step's notice, when set, replaces its actions (e.g. a theme that can't take app blocks).
 */
const SetupGuide = ({
  steps = [],
  onToggleStep = (key) => {},
  onDismiss = () => {}
}) => {
  const doneCount = steps.filter(step => step.completed).length;
  const allDone = doneCount === steps.length;
  const [collapsed, setCollapsed] = useState(allDone);
  // Follow the first open step until the merchant picks one
  const [pickedStep, setPickedStep] = useState(null);
  const openStep = pickedStep ?? (steps.find(step => !step.completed) || steps[0])?.key;

  const wasAllDone = useRef(allDone);
  useEffect(() => {
    if (allDone && !wasAllDone.current) setCollapsed(true);
    wasAllDone.current = allDone;
  }, [allDone]);

  const group = (label, groupSteps) => groupSteps.length > 0 && (
    <BlockStack gap="100">
      <div className="setup-guide-group-label">
        <Text as="h3" variant="bodySm" tone="subdued" fontWeight="semibold">{label}</Text>
      </div>
      {groupSteps.map(step => (
        <SetupGuideStep
          key={step.key}
          step={step}
          open={step.key === openStep}
          onOpen={() => setPickedStep(step.key)}
          onToggle={() => onToggleStep(step.key)}
        />
      ))}
    </BlockStack>
  );

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center" wrap={false}>
            <Text as="h2" variant="headingSm">Setup guide</Text>
            <div className="setup-guide-actions">
              <Button
                variant="tertiary"
                icon={collapsed ? ChevronDownMinor : ChevronUpMinor}
                onClick={() => setCollapsed(!collapsed)}
                accessibilityLabel={collapsed ? "Show steps" : "Hide steps"}
                ariaExpanded={!collapsed}
              />
              <Tooltip content="Dismiss guide" dismissOnMouseOut>
                <Button variant="tertiary" icon={CancelSmallMinor} onClick={onDismiss} accessibilityLabel="Dismiss guide" />
              </Tooltip>
            </div>
          </InlineStack>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Text as="span" variant="bodySm" tone="subdued">
              {allDone ? `All set up · ${doneCount} of ${steps.length} done` : `${doneCount} of ${steps.length} done`}
            </Text>
            <div className="setup-guide-progress">
              <ProgressBar size="small" tone="success" progress={(doneCount / steps.length) * 100} />
            </div>
          </InlineStack>
        </BlockStack>

        {!collapsed && (
          <BlockStack gap="300">
            {group("Get started", steps.filter(step => !step.optional))}
            {group("Optional", steps.filter(step => step.optional))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
};

const SetupGuideStep = ({ step, open, onOpen, onToggle }) => {
  const mark = step.completed
    ? <Icon source={CircleTickMinor} tone="success" />
    : <span className="setup-guide-step__circle" />;

  return (
    // The whole row opens the step for mouse users; the title button does it for keyboard users
    <div
      className={`setup-guide-step${open ? " is-open" : ""}${step.completed ? " is-done" : ""}`}
      onClick={onOpen}
    >
      <InlineStack gap="300" wrap={false} blockAlign="start">
        {step.canToggle ? (
          <button
            type="button"
            className="setup-guide-step__toggle"
            title={`Mark as ${step.completed ? "not done" : "done"}`}
            aria-label={`Mark "${step.title}" as ${step.completed ? "not done" : "done"}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            {mark}
          </button>
        ) : (
          <span className="setup-guide-step__toggle">{mark}</span>
        )}
        <div className="setup-guide-step__body">
          <BlockStack gap="200">
            <button type="button" className="setup-guide-step__title" aria-expanded={open} onClick={onOpen}>
              <Text as="span" fontWeight={open ? "semibold" : undefined} tone={step.completed && !open ? "subdued" : undefined}>
                {step.title}
              </Text>
            </button>
            {open && (
              <>
                <Text as="p" tone="subdued">{step.description}</Text>
                {step.notice ?? (
                  <InlineStack gap="200">
                    {step.actions.map(({ content, url, external, onAction }, i) => (
                      <Button
                        key={content}
                        variant={i === 0 ? "primary" : undefined}
                        url={url}
                        target={external ? "_blank" : undefined}
                        onClick={onAction}
                      >
                        {content}
                      </Button>
                    ))}
                  </InlineStack>
                )}
              </>
            )}
          </BlockStack>
        </div>
        {open && step.media && (
          <div className="setup-guide-step__media">
            <img src={step.media} alt="" />
          </div>
        )}
      </InlineStack>
    </div>
  );
};

export default SetupGuide;
